/**
 * NetTraceReporter (Wave 2.2, 2026-05-15) — drains the existing
 * NetworkTracer.recentTraffic ring every 5s and pushes the most-recent
 * 50 events via Colyseus `netobs:netTrace`. Server keeps a per-session
 * ring (NetTraceBuffer); MCP fetches via `/api/diag/net-trace`.
 *
 * Mirrors BrowserPerfReporter / PhysicsTraceReporter patterns. Gated on
 * isNetobsEnabled(). The 50-event cap keeps wire size at ~3-4 KB per push.
 */

import React, { useEffect } from 'react';
import { isNetobsEnabled } from './isNetobsEnabled';
import type {
  NetTraceEventWire,
  NetTraceMessageTypeStatsWire,
  NetTraceSnapshotWire,
} from '../../../shared/diagnostics/NetTraceTypes';

const REPORT_INTERVAL_MS = 5000;
const RECENT_EVENT_CAP = 50;
const TOP_STATS_CAP = 10;

interface MessageTypeStats {
  count: number;
  totalBytes: number;
  avgBytes: number;
  maxBytes: number;
  lastAt: string;
}

function topStats(map: Map<string, MessageTypeStats> | undefined, cap: number): NetTraceMessageTypeStatsWire[] {
  if (!map || typeof map.entries !== 'function') return [];
  const arr: NetTraceMessageTypeStatsWire[] = [];
  for (const [type, s] of map.entries()) {
    arr.push({ type, count: s.count, totalBytes: s.totalBytes, avgBytes: s.avgBytes, maxBytes: s.maxBytes, lastAt: s.lastAt });
  }
  arr.sort((a, b) => b.count - a.count);
  return arr.slice(0, cap);
}

const NetTraceReporterInner: React.FC = () => {
  useEffect(() => {
    let lastFlushAt = Date.now();
    const timer = window.setInterval(() => {
      const now = Date.now();
      const windowMs = now - lastFlushAt;
      lastFlushAt = now;

      const tracer = (globalThis as any).nm?.tracer;
      if (!tracer) return;

      const recent: any[] = Array.isArray(tracer.recentTraffic)
        ? tracer.recentTraffic.slice(-RECENT_EVENT_CAP)
        : [];
      const events: NetTraceEventWire[] = recent.map((e) => ({
        seq: e.seq,
        timestamp: e.timestamp,
        direction: e.direction,
        type: e.type,
        bytes: e.bytes,
        traceId: e.traceId,
        clientSeq: e.clientSeq,
        serverSeq: e.serverSeq,
      }));

      const snapshot: NetTraceSnapshotWire = {
        capturedAt: now,
        windowMs,
        messagesSent: tracer.messagesSent ?? 0,
        messagesReceived: tracer.messagesReceived ?? 0,
        currentThroughput: tracer.currentThroughput ?? 0,
        events,
        topOutbound: topStats(tracer.outboundMessageTypeStats, TOP_STATS_CAP),
        topInbound: topStats(tracer.inboundMessageTypeStats, TOP_STATS_CAP),
      };

      try {
        const room = (globalThis as any).nm?.getRoom?.();
        if (room && typeof room.send === 'function') {
          room.send('netobs:netTrace', snapshot);
        }
      } catch {
        // diagnostic-only, never gameplay
      }
    }, REPORT_INTERVAL_MS);

    return () => window.clearInterval(timer);
  }, []);

  return null;
};

export const NetTraceReporter: React.FC = () => {
  if (!isNetobsEnabled()) return null;
  return <NetTraceReporterInner />;
};
